import React, {useRef, useState} from 'react';
import {Controller, Control, FieldValues, Path} from 'react-hook-form';
import {UploadIcon, Trash2Icon
} from "lucide-react"

import styles from './MedicalDocumentationField.module.scss';

interface MedicalDocumentationFieldProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  maxFiles?: number;
  title?: string;
  uploadUrl: string;
  required?: boolean;
  minFiles?: number;
  requiredMessage?: string;
}

function MedicalDocumentationField<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  maxFiles = 5,
  title = 'Dodaj dokumentację medyczną',
  uploadUrl,
  required = false,
  minFiles = 1,
  requiredMessage = 'Dodaj co najmniej 1 plik'
}: MedicalDocumentationFieldProps<TFieldValues>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  console.log(required, "required");

  const uploadFiles = async (files: File[]) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });

      const xhr = new XMLHttpRequest();
      xhr.open('POST', uploadUrl);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      return new Promise<any[]>((resolve, reject) => {
        xhr.onload = () => {
          setUploading(false);
          setUploadProgress(0);
          try {
            const result = JSON.parse(xhr.responseText);
            if (result.success && result.files) {
              resolve(result.files);
            } else {
              reject(result.error || 'Upload failed');
            }
          } catch (e) {
            reject('Upload failed');
          }
        };
        xhr.onerror = () => {
          setUploading(false);
          setUploadProgress(0);
          reject('Upload failed');
        };
        xhr.send(formData);
      });
    } catch (error) {
      setUploading(false);
      setUploadProgress(0);
      throw error;
    }
  };

  const handleFileChange = async (
    onChange: (files: any[]) => void,
    value: any[]
  ) => {
    const file = inputRef.current?.files?.[0];
    if (!file) return;
    if ((value?.length || 0) >= maxFiles) return;

    try {
      const uploadedFiles = await uploadFiles([file]);
      const newFiles = [...(value || []), ...uploadedFiles];
      onChange(newFiles);
    } catch (error) {
      // TODO: show error
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeFile = (
    onChange: (files: any[]) => void,
    value: any[],
    index: number
  ) => {
    const newFiles = value.filter((_: any, i: number) => i !== index);
    onChange(newFiles);
  };

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={[] as any}
      rules={{
        validate: (files: any[]) => {
          if (!required) return true;
          return (
            (Array.isArray(files) && files.length >= minFiles) ||
            requiredMessage
          );
        }
      }}
      render={({field: {value, onChange}, fieldState}) => (
        <div className="r-form-fields-container">
          <h3 className={`mb-2 text-[16px] font-semibold text-left sm:text-lg`}>
            {title} {required && <span className={styles.required}>*</span>}
          </h3>

          {uploading && (
            <div className={styles.uploading}>
              <div className={styles.progressBar}>
                <div
                  className={styles.progress}
                  style={{width: `${uploadProgress}%`}}
                />
              </div>
            </div>
          )}

          {!value || value.length === 0 ? (
            <label className={styles.inputLike}>
              <input
                type='file'
                ref={inputRef}
                style={{display: 'none'}}
                onChange={() => handleFileChange(onChange, value)}
                disabled={uploading}
                accept='.pdf,.jpg,.jpeg,.png'
              />
              <span className={styles.placeholder}>Wybierz plik</span>
              <span className={styles.uploadIcon}>
              <UploadIcon className="size-6" color="#B0B0B0" />
              </span>
            </label>
          ) : (
            <>
              {value.length < maxFiles && (
                <label className={styles.inputLike}>
                  <input
                    type='file'
                    ref={inputRef}
                    style={{display: 'none'}}
                    onChange={() => handleFileChange(onChange, value)}
                    disabled={uploading}
                    accept='.pdf,.jpg,.jpeg,.png'
                  />
                  <span className={styles.placeholder}>Wybierz plik</span>
                  <span className={styles.uploadIcon}><UploadIcon className="size-6" color="#B0B0B0" /></span>
                </label>
              )}
              {value.map((file: any, idx: number) => {
                return (
                  <div
                    key={file.url || file.name || idx}
                    className={styles.inputLike}
                  >
                    <span className={styles.fileName}>
                      {file?.[1] || file.url}
                    </span>
                    <button
                      type='button'
                      className={styles.deleteBtn}
                      onClick={() => removeFile(onChange, value, idx)}
                      disabled={uploading}
                    >
                       <Trash2Icon className="size-6" color="#B0B0B0" />
                    </button>
                  </div>
                );
              })}
            </>
          )}
          {fieldState.error?.message && (
            <div style={{color: 'var(--destructive)', marginTop: 8, fontSize: 13, fontWeight: 500, textAlign: 'left'}}>
              {fieldState.error.message}
            </div>
          )}
        </div>
      )}
    />
  );
}

export default MedicalDocumentationField;
